/**
 * Snake config — the structured per-game data the SkinResolver consumes.
 * See data/frogger.config.js for the shape rationale.
 */

import { SNAKE_CONFIG, SNAKE_GRID, SNAKE_LARGE_GRID } from '../src/games/snake/snakeConfig.js';

export const SNAKE = {
    grid: {
        cols: SNAKE_GRID.cols,
        rows: SNAKE_GRID.rows,
        cellSize: SNAKE_GRID.cellSize,
        originX: SNAKE_GRID.originX,
        originY: SNAKE_GRID.originY,
        targetCellSize: SNAKE_CONFIG.TARGET_CELL_SIZE,
        paddingTop: SNAKE_CONFIG.PADDING_TOP,
        paddingSide: SNAKE_CONFIG.PADDING_SIDE,
        paddingBottom: SNAKE_CONFIG.PADDING_BOTTOM,
        freeroamMultiplier: SNAKE_CONFIG.FREEROAM_GRID_MULTIPLIER,
        largeCols: SNAKE_LARGE_GRID.cols,
        largeRows: SNAKE_LARGE_GRID.rows,
    },

    tuning: {
        baseTickRate: SNAKE_CONFIG.BASE_TICK_RATE,
        tickRateStep: SNAKE_CONFIG.TICK_RATE_STEP,
        maxTickRate: SNAKE_CONFIG.MAX_TICK_RATE,
        initialLength: SNAKE_CONFIG.INITIAL_LENGTH,
        growAmount: SNAKE_CONFIG.GROW_AMOUNT,
        pointsPerFood: SNAKE_CONFIG.POINTS_PER_FOOD,
        stationPointsPerPassenger: SNAKE_CONFIG.STATION_POINTS_PER_PASSENGER,
        stationGrowPerPassenger: SNAKE_CONFIG.STATION_GROW_PER_PASSENGER,
        boardClearBonus: SNAKE_CONFIG.BOARD_CLEAR_BONUS,
        obstacleCountMin: SNAKE_CONFIG.OBSTACLE_COUNT_MIN,
        obstacleCountMax: SNAKE_CONFIG.OBSTACLE_COUNT_MAX,
        obstacleExtraPerBoard: SNAKE_CONFIG.OBSTACLE_EXTRA_PER_BOARD,
        obstacleCap: SNAKE_CONFIG.OBSTACLE_CAP,
        obstacleSpawnMargin: SNAKE_CONFIG.OBSTACLE_SPAWN_MARGIN,
        obstacleTypes: SNAKE_CONFIG.OBSTACLE_TYPES,
        exit: {
            width: SNAKE_CONFIG.EXIT_WIDTH,
            color: SNAKE_CONFIG.EXIT_COLOR,
            pulseSpeed: SNAKE_CONFIG.EXIT_PULSE_SPEED,
        },
        adventure: {
            screens: SNAKE_CONFIG.ADVENTURE_SCREENS,
            pointsToUnlock: SNAKE_CONFIG.ADVENTURE_POINTS_TO_UNLOCK,
            doorSides: SNAKE_CONFIG.ADVENTURE_DOOR_SIDES,
            houseW: SNAKE_CONFIG.ADVENTURE_HOUSE_W,
            houseH: SNAKE_CONFIG.ADVENTURE_HOUSE_H,
            houseColor: SNAKE_CONFIG.ADVENTURE_HOUSE_COLOR,
            houseOpenColor: SNAKE_CONFIG.ADVENTURE_HOUSE_OPEN_COLOR,
            doorColor: SNAKE_CONFIG.ADVENTURE_DOOR_COLOR,
        },
        freeroam: {
            defaultTarget: SNAKE_CONFIG.FREEROAM_DEFAULT_TARGET,
            defaultTimer: SNAKE_CONFIG.FREEROAM_DEFAULT_TIMER,
            obstacleDensity: SNAKE_CONFIG.FREEROAM_OBSTACLE_DENSITY,
            foodCount: SNAKE_CONFIG.FREEROAM_FOOD_COUNT,
        },
        perks: {
            spawnIntervalMs: SNAKE_CONFIG.PERK_SPAWN_INTERVAL_MS,
            lifetimeMs: SNAKE_CONFIG.PERK_LIFETIME_MS,
            blinkMs: SNAKE_CONFIG.PERK_BLINK_MS,
            maxOnBoard: SNAKE_CONFIG.PERK_MAX_ON_BOARD,
            ghostAlpha: SNAKE_CONFIG.GHOST_ALPHA,
            slowMultiplier: SNAKE_CONFIG.SLOW_MULTIPLIER,
            slowMinRate: SNAKE_CONFIG.SLOW_MIN_RATE,
            speedUpSteps: SNAKE_CONFIG.SPEED_UP_STEPS,
            types: SNAKE_CONFIG.PERK_TYPES,
        },
        smoothMotion: {
            lerpFactor: 0.35,
            cornerLerp: 0.25,
        },
    },

    modes: {
        classic:   { label: 'Classic' },
        adventure: { label: 'Adventure' },
        stations:  { label: 'Stations' },
        freeroam:  { label: 'Free Roam' },
    },

    settings: {
        gameMode:         { default: 'stations', type: 'choice',  options: ['classic', 'adventure', 'stations', 'freeroam'], label: 'Game Mode' },
        obstacleDensity:  { default: 'normal',   type: 'choice',  options: ['few', 'normal', 'many', 'max'],                  label: 'Obstacle Density' },
        smoothMovement:   { default: true,       type: 'boolean', label: 'Smooth Movement' },
        smoothCorners:    { default: true,       type: 'boolean', label: 'Smooth Corners' },
        tramTracks:       { default: true,       type: 'boolean', label: 'Show Tram Tracks' },
        speedDestroys:    { default: true,       type: 'boolean', label: 'Speed+ Destroys Obstacles' },
        touchMode:        { default: true,       type: 'boolean', label: 'Touch Mode (Slower)' },
        constantSpeed:    { default: true,       type: 'boolean', label: 'Constant Speed (no ramp-up)' },
        cloudBlur:        { default: true,       type: 'boolean', label: 'Blurred Clouds (out of focus)' },
        pointsToPass:     { default: 100,        type: 'number',  min: 10,  max: 1000,  step: 10,  label: 'Points To Pass (Adventure)' },
        freeroamTarget:   { default: 1000,       type: 'number',  min: 10,  max: 10000, step: 10,  label: 'Score Target (Free Roam)' },
        freeroamTimer:    { default: 60,         type: 'number',  min: 10,  max: 600,   step: 10,  label: 'Timer (Free Roam)' },

        // ── Perks (on-board timing) ─────────────────────
        // How long a freshly spawned perk stays on the board before
        // expiring, and how long the warning red-phase / countdown
        // 5..1 lasts at the end of life. Doubled vs the legacy values
        // so players have time to read the icon and decide whether
        // it is worth the detour.
        perkLifetimeSeconds:  { default: 16, type: 'number', min: 4, max: 60, step: 1, label: 'Perk Visibility (sec)' },
        perkCountdownSeconds: { default: 4,  type: 'number', min: 1, max: 15, step: 1, label: 'Perk Countdown (sec)' },

        // ── Lives / invincibility ───────────────────────
        // 3 lives per stage, refilled to this value every time the
        // player crosses a portal. Hitting a wall / obstacle / self
        // costs 1 life; running it down to 0 ends the run. After a
        // life is lost the snake is invincible for `invincibleSeconds`
        // so the player can read the board again before traffic
        // resumes.
        livesPerStage:    { default: 3, type: 'number', min: 1, max: 9,  step: 1, label: 'Lives Per Stage' },
        invincibleSeconds:{ default: 5, type: 'number', min: 1, max: 15, step: 1, label: 'Invincible After Hit (sec)' },
    },

    elements: {
        // Footprints are in GAME CELLS (1 cell = 50 px). The asset is rendered
        // with `fitMode:'contain'` so it scales to fit `footprint.w × footprint.h`
        // cells. Picking a footprint that matches the asset's aspect ratio gives
        // the cleanest rendering; mismatches just get extra empty padding around
        // the sprite (no distortion).
        'atlas.main':            { label: 'Shared Atlas Source', category: 'atlas',     footprint: { w: 1, h: 1 } },
        background:              { label: 'Background',           category: 'board',     fitMode: 'cover', defaultPath: 'assets/snake/background.png' },
        'board.cell':            { label: 'Board Cell Texture',   category: 'board',     footprint: { w: 1, h: 1 }, fitMode: 'cover' },
        'board.border':          { label: 'Board Border',         category: 'board',     fitMode: 'cover' },

        // ── Sky / clouds ────────────────────────────────────
        // Drift slowly across the top of the play arena.
        // Footprints reflect the source PNG aspect: large clouds are 4×2
        // cells (200×100 px), smaller ones 2.4×1.2 (we render at 120×60).
        'sky.cloud.large.1':     { label: 'Cloud Large 1 (4x2)',  category: 'board',     footprint: { w: 4, h: 2 }, defaultPath: 'assets/snake/sky-cloud1.png' },
        'sky.cloud.large.2':     { label: 'Cloud Large 2 (4x2)',  category: 'board',     footprint: { w: 4, h: 2 }, defaultPath: 'assets/snake/sky-cloud2.png' },
        'sky.cloud.small.1':     { label: 'Cloud Small 1 (2x1)',  category: 'board',     footprint: { w: 2, h: 1 }, defaultPath: 'assets/snake/sky-cloud-smaller.png' },
        'sky.cloud.small.2':     { label: 'Cloud Small 2 (2x1)',  category: 'board',     footprint: { w: 2, h: 1 }, defaultPath: 'assets/snake/sky-cloud-smaller2.png' },

        // ── Tram ────────────────────────────────────────────
        'tram.head':             { label: 'Tram First Car (2x1)',     category: 'characters', footprint: { w: 2, h: 1 }, fallbackColor: SNAKE_CONFIG.COLOR_HEAD, defaultPath: 'assets/snake/tram.png' },
        'tram.body':             { label: 'Tram Secondary Car (2x1)', category: 'characters', footprint: { w: 2, h: 1 }, fallbackColor: SNAKE_CONFIG.COLOR_BODY, defaultPath: 'assets/snake/tram-cart.png' },
        'tram.connector':        { label: 'Tram Connector',           category: 'characters', footprint: { w: 1, h: 1 } },

        // ── Track / board ───────────────────────────────────
        'track.straight':        { label: 'Track Straight',       category: 'board',     footprint: { w: 1, h: 1 } },
        'track.corner':          { label: 'Track Corner',         category: 'board',     footprint: { w: 1, h: 1 } },

        // ── Pickups ─────────────────────────────────────────
        food:                    { label: 'Food / Pickup',        category: 'pickups',   footprint: { w: 1, h: 1 }, fallbackColor: SNAKE_CONFIG.COLOR_FOOD },
        // Stations — fixed 4×2 footprint (200×100 px on screen). PNGs are
        // 281×149 (aspect ≈ 1.89:1) so a 4×2 cell box (aspect 2:1) lets the
        // art render at near-native size with only a hair of horizontal
        // letterbox. Spawner picks variant 'single' or 'double': single
        // grows the snake by 1 tram car, double grows it by 2.
        // `station.tall` is retained only as a theme-editor customisation
        // hook; the live spawner does not emit it any more.
        'station.single':        { label: 'Station Single (4x2)',  category: 'pickups',   footprint: { w: 4, h: 2 }, defaultPath: 'assets/snake/tram-station-single.png' },
        'station.wide':          { label: 'Station Double (4x2)',  category: 'pickups',   footprint: { w: 4, h: 2 }, defaultPath: 'assets/snake/tram-station-double.png' },
        'station.tall':          { label: 'Station Tall (legacy)', category: 'pickups',   footprint: { w: 1, h: 2 } },

        'perk.shrink':           { label: 'Perk: Shrink',         category: 'pickups',   footprint: { w: 1, h: 1 }, fallbackColor: 0x22c55e, defaultPath: 'assets/snake/perk-shrink.png' },
        'perk.speed_up':         { label: 'Perk: Speed Up',       category: 'pickups',   footprint: { w: 1, h: 1 }, fallbackColor: 0xef4444, defaultPath: 'assets/snake/perk-speed.png' },
        'perk.random_turn':      { label: 'Perk: Chaos',          category: 'pickups',   footprint: { w: 1, h: 1 }, fallbackColor: 0xf97316, defaultPath: 'assets/snake/perk-chaos.png' },
        'perk.ghost':            { label: 'Perk: Ghost',          category: 'pickups',   footprint: { w: 1, h: 1 }, fallbackColor: 0x3b82f6, defaultPath: 'assets/snake/perk-ghost.png' },
        'perk.slow':             { label: 'Perk: Slow Mo',        category: 'pickups',   footprint: { w: 1, h: 1 }, fallbackColor: 0x06b6d4, defaultPath: 'assets/snake/perk-slow.png' },

        // ── Goal / exit ─────────────────────────────────────
        exit:                    { label: 'Exit Gate',            category: 'goal',      footprint: { w: 3, h: 1 }, fallbackColor: SNAKE_CONFIG.EXIT_COLOR },
        door:                    { label: 'Adventure Door',       category: 'goal',      footprint: { w: 3, h: 1 } },
        house:                   { label: 'Final House (5x3)',    category: 'goal',      footprint: { w: 5, h: 3 }, fallbackColor: SNAKE_CONFIG.ADVENTURE_HOUSE_COLOR, defaultPath: 'assets/snake/house.png' },

        // ── Trees ───────────────────────────────────────────
        'obstacle.tree.single':  { label: 'Small Tree (2x2)',     category: 'obstacles', footprint: { w: 2, h: 2 }, fallbackColor: 0x065f46, defaultPath: 'assets/snake/obstacle-tree-small.png' },
        'obstacle.tree.pine':    { label: 'Pine Tree (1x2)',      category: 'obstacles', footprint: { w: 1, h: 2 }, fallbackColor: 0x0f5132, defaultPath: 'assets/snake/obstacle-pine-tree.png' },

        // ── Bushes ──────────────────────────────────────────
        'obstacle.bush.single':  { label: 'Green Bush (2x2)',         category: 'obstacles', footprint: { w: 2, h: 2 }, fallbackColor: 0x047857, defaultPath: 'assets/snake/obstacle-bush.png' },
        'obstacle.bush.pink':    { label: 'Pink Flower Bush (4x2)',   category: 'obstacles', footprint: { w: 4, h: 2 }, fallbackColor: 0xdb2777, defaultPath: 'assets/snake/obstacle-bush-pink.png' },
        'obstacle.bush.yellow':  { label: 'Yellow Flower Bush (1x1)', category: 'obstacles', footprint: { w: 1, h: 1 }, fallbackColor: 0xeab308, defaultPath: 'assets/snake/obstacle-bush-yellow.png' },
        'obstacle.bush.wide':    { label: 'Wide Bush (4x2)',          category: 'obstacles', footprint: { w: 4, h: 2 }, fallbackColor: 0x047857 },

        // ── Rocks ───────────────────────────────────────────
        'obstacle.rock.single':  { label: 'Small Stone (2x2)',    category: 'obstacles', footprint: { w: 2, h: 2 }, fallbackColor: 0x94a3b8, defaultPath: 'assets/snake/obstacle-rock-small.png' },
        'obstacle.rock.large':   { label: 'Boulder (3x2)',        category: 'obstacles', footprint: { w: 3, h: 2 }, fallbackColor: 0x64748b, defaultPath: 'assets/snake/obstacle-rock.png' },

        // ── Street decor ───────────────────────────────────
        'obstacle.flowerbed':         { label: 'Flowerbed (2x1)',        category: 'obstacles', footprint: { w: 2, h: 1 }, fallbackColor: 0xec4899, defaultPath: 'assets/snake/obstacle-flowerbed.png' },
        'obstacle.flowerbed.orange':  { label: 'Orange Flowerbed (1x1)', category: 'obstacles', footprint: { w: 1, h: 1 }, fallbackColor: 0xf97316, defaultPath: 'assets/snake/obstacle-flowerbed-orange.png' },
        'obstacle.lamp':              { label: 'Street Lamp (1x2)',      category: 'obstacles', footprint: { w: 1, h: 2 }, fallbackColor: 0x6b7280, defaultPath: 'assets/snake/obstacle-lamp.png' },
        'obstacle.traffic_lamp':      { label: 'Traffic Light (1x2)',    category: 'obstacles', footprint: { w: 1, h: 2 }, fallbackColor: 0x991b1b, defaultPath: 'assets/snake/obstacle-traffic-lamp.png' },
        'obstacle.bench':             { label: 'Park Bench (2x1)',       category: 'obstacles', footprint: { w: 2, h: 1 }, fallbackColor: 0x78350f, defaultPath: 'assets/snake/obstacle-bench.png' },
        'obstacle.barrier':           { label: 'Barrier (3x2)',          category: 'obstacles', footprint: { w: 3, h: 2 }, fallbackColor: 0xf59e0b, defaultPath: 'assets/snake/obstacle-barrier.png' },
        'obstacle.stop':              { label: 'Stop Sign (2x2)',        category: 'obstacles', footprint: { w: 2, h: 2 }, fallbackColor: 0xdc2626, defaultPath: 'assets/snake/obstacle-stop.png' },
        'obstacle.icecream_truck':    { label: 'Ice-cream Truck (2x3)',  category: 'obstacles', footprint: { w: 2, h: 3 }, fallbackColor: 0xf472b6, defaultPath: 'assets/snake/obstacle-icecream-truck.png' },

        // ── Buildings ──────────────────────────────────────
        'obstacle.building.large':       { label: 'Generic Building (3x3)', category: 'obstacles', footprint: { w: 3, h: 3 }, fallbackColor: 0x475569, defaultPath: 'assets/snake/obstacle-building.png' },
        'obstacle.building.hospital':    { label: 'Hospital (4x4)',         category: 'obstacles', footprint: { w: 4, h: 4 }, fallbackColor: 0x3b82f6, defaultPath: 'assets/snake/obstacle-building-hospital.png' },
        'obstacle.building.pink':        { label: 'Pink Building (4x4)',    category: 'obstacles', footprint: { w: 4, h: 4 }, fallbackColor: 0xec4899, defaultPath: 'assets/snake/obstacle-building-pink.png' },
        'obstacle.building.modern':      { label: 'Modern Building (3x2)',  category: 'obstacles', footprint: { w: 3, h: 2 }, fallbackColor: 0x06b6d4, defaultPath: 'assets/snake/obstacle-building-modern.png' },
        'obstacle.building.restaurant':  { label: 'Restaurant (3x2)',       category: 'obstacles', footprint: { w: 3, h: 2 }, fallbackColor: 0xb45309, defaultPath: 'assets/snake/obstacle-building-restaurant.png' },
        'obstacle.building.vegies':      { label: 'Veggie Stand (2x2)',     category: 'obstacles', footprint: { w: 2, h: 2 }, fallbackColor: 0x16a34a, defaultPath: 'assets/snake/obstacle-building-vegies.png' },

        // ── HUD ────────────────────────────────────────────
        // Top-bar chip icons. The PNGs live in `assets/snake/ui-*.png`
        // and are also reused on the Game Over screen so the result
        // panel reads as the same family of stats the player saw mid-run.
        'hud.panel':             { label: 'HUD Panel',            category: 'ui' },
        'hud.timer':             { label: 'HUD Timer Icon',       category: 'ui' },
        'hud.score':             { label: 'HUD Score Icon (Cup)', category: 'ui', defaultPath: 'assets/snake/ui-cup.png' },
        'hud.cars':              { label: 'HUD Cars Icon (Tram)', category: 'ui', defaultPath: 'assets/snake/ui-tram-car.png' },
        'hud.board':             { label: 'HUD Screen Icon',      category: 'ui', defaultPath: 'assets/snake/ui-screen.png' },
    },

    palette: {
        head: SNAKE_CONFIG.COLOR_HEAD,
        body: SNAKE_CONFIG.COLOR_BODY,
        food: SNAKE_CONFIG.COLOR_FOOD,
        gridBg: SNAKE_CONFIG.COLOR_GRID_BG,
        gridLine: SNAKE_CONFIG.COLOR_GRID_LINE,
        wall: SNAKE_CONFIG.COLOR_WALL,
    },
};
